import axios from "axios";
import {
  encryptPayload, decryptPayload,
  getSessionId, hasSession, initSession,
} from "./cryptoService";
import { getAuditHeaders, isAuditedUrl } from "./auditHeaders";
import { hasUnlockListener, requestUnlock } from "./unlockCoordinator";
import { setServerPushHandlers, setLoginNotice } from "./serverPush";
import { getDeviceSecret, rememberSigninOwner } from "./deviceSecret";
import { errorCodeOf, isSessionExpired } from "./gatewayErrors";
import store from "../Store";
import { logout } from "../Store/Slices/AuthSlice";

// All merchant-portal-backend traffic proxied through api-auth-gateway lives
// under /api/bpay — same prefix merchant-portal-backend's own controllers use,
// the gateway route (routes[2]) doesn't rewrite the path.
const gatewayApi = axios.create({
  baseURL: `${process.env.REACT_APP_GATEWAY_URL}/api/bpay`,
});

// ── Request: encrypt body + attach X-Session-Id ───────────────────────────────
// File uploads (FormData — onboarding docs, KYC, invoices, bulk-payout sheets)
// can't fit the single-JSON-envelope protocol, so they're sent as plain
// multipart — the gateway exempts them by Content-Type, not by path.

gatewayApi.interceptors.request.use(async (config) => {
  if (!hasSession()) {
    await initSession();
  }

  config.headers["X-Session-Id"] = getSessionId();

  // Login / MPIN / re-auth calls also tell the gateway's audit trail where they come from (location and
  // device model; best-effort, never blocks for long and never fails the request).
  // Sign-in also tells the gateway which device this is, so a device that already passed OTP + MPIN is not asked again.
  if (config.url?.includes("/staff/onboard/signin")) {
    const owner = config.data?.mobile_number;
    rememberSigninOwner(owner);              // the pass handed out after OTP + MPIN is filed under this user
    const deviceSecret = getDeviceSecret(owner);
    if (deviceSecret) config.headers["X-Device-Secret"] = deviceSecret;
  }

  if (isAuditedUrl(config.url)) {
    const audit = await getAuditHeaders();
    Object.entries(audit).forEach(([name, value]) => {
      config.headers[name] = value;
    });
  }

  const isFormData = config.data instanceof FormData;

  if (!isFormData && config.data !== undefined && config.data !== null) {
    // Save plaintext so the response interceptor can re-encrypt on retry with a new key
    config._originalData = config.data;
    config.data = await encryptPayload(config.data);
    config.headers["Content-Type"] = "application/json";
  }

  return config;
});

// mode "unlock": crypto session expired → merchant validate-mpin. mode "reauth": IP changed → gateway
// /auth/reauth. MpinVerify.js reads sessionStorage.mpinMode. Never overwrite the page to return to
// with the MPIN screen itself.
function goToMpinScreen(mode) {
  sessionStorage.setItem("mpinMode", mode);
  if (window.location.pathname !== "/mpin-verify") {
    sessionStorage.setItem("postMpinRedirect", window.location.pathname);
  }
  window.location.href = "/mpin-verify";
}

function forceLogout(notice) {
  store.dispatch(logout());
  localStorage.clear();
  if (typeof notice === "string" && notice) setLoginNotice(notice);      // shown once on the sign-in page
  sessionStorage.removeItem("postMpinRedirect");
  sessionStorage.removeItem("mpinMode");
  window.location.href = "/";
}

// What the gateway pushes over the heartbeat WebSocket: this login was ended -> sign out now; the crypto session is
// gone -> ask for the MPIN now (the same modal a failed call would open), instead of waiting for the next click.
setServerPushHandlers({
  onLogout: (notice) => forceLogout(notice),
  onLock: () => {
    if (!hasUnlockListener()) return;
    requestUnlock("unlock", initSession).catch(() => { /* the user gave up: the modal logs them out */ });
  },
});

// ── Response: decrypt body ────────────────────────────────────────────────────
// Envelope is now { data: "<base64 iv||ciphertext>" } — a single field, so
// checking Object.keys length too avoids mistaking a real one-key JSON body
// (unlikely here since every non-multipart response is always encrypted, but
// cheap to be sure) for an encrypted envelope.

function isEnvelope(body) {
  return !!body && typeof body === "object" &&
    typeof body.data === "string" && Object.keys(body).length === 1;
}

gatewayApi.interceptors.response.use(
  async (res) => {
    if (isEnvelope(res.data)) {
      try {
        res.data = await decryptPayload(res.data);
      } catch (decryptErr) {
        // The browser's session key no longer matches the one the gateway used (session replaced or lost).
        // That is an expired crypto session in all but name: ask for the MPIN again, do not crash or log out.
        if (localStorage.getItem("employeeDetails")) {
          try { await initSession(); } catch { /* /mpin-verify will retry the handshake */ }
          goToMpinScreen("unlock");
        }
        throw decryptErr;
      }
    }
    return res;
  },
  async (err) => {
    // Decrypt encrypted error body so callers can read .message / .error_code
    if (isEnvelope(err.response?.data)) {
      try { err.response.data = await decryptPayload(err.response.data); } catch { /* keep raw */ }
    }

    const status = err.response?.status;

    // Sign-in, validate-mpin and /auth/reauth deal with their own errors on their own screens;
    // redirecting from here would loop.
    const url = err.config?.url || "";
    const handlesItsOwnErrors =
      url.includes("/staff/onboard/signin") ||
      url.includes("/staff/onboard/validate-mpin") ||
      url.includes("/auth/reauth") ||
      url.includes("/auth/device/");
    const loggedIn = !!localStorage.getItem("employeeDetails");

    // No answer at all (the gateway is restarting, a connection dropped): a GET is repeated ONCE after a short wait, so a
    // blip is not seen by the user. Never a POST/PUT/DELETE: it may have reached the backend before the connection broke.
    if (!err.response && err.code === "ERR_NETWORK" && err.config && !err.config._networkRetried
        && String(err.config.method || "get").toLowerCase() === "get") {
      await new Promise((resolve) => setTimeout(resolve, err.config.networkRetryDelayMs ?? 1500));
      return gatewayApi.request({ ...err.config, _networkRetried: true });
    }

    // The crypto session lapsed while nobody is logged in (a forgot-password form left open while the OTP SMS
    // arrives, a sign-in page that sat idle). There is no MPIN screen to send them to, and the gateway refused the
    // call before it reached the backend, so it is safe to shake hands again and repeat it - once.
    if (!loggedIn && status === 401 && isSessionExpired(err) && err.config && !err.config._sessionRetried) {
      try {
        await initSession();
      } catch {
        return Promise.reject(err);               // the handshake failed too: the caller sees the original error
      }
      const retry = { ...err.config, _sessionRetried: true };
      if (retry._originalData !== undefined) {
        retry.data = retry._originalData;         // the request interceptor encrypts it again under the new key
      }
      return gatewayApi.request(retry);
    }

    // A logged-in user whose crypto session lapsed (GWC002) or whose IP changed (GWB001) is asked for the MPIN in a modal
    // OVER the page they are on (see unlockCoordinator.js), and the call that failed is repeated afterwards: nothing
    // they were doing is lost. The gateway refused the call before it reached the backend, so repeating it is safe.
    // Only once per call, and never for the MPIN calls themselves; without a modal mounted the old MPIN page is used.
    if (loggedIn && status === 401 && !handlesItsOwnErrors && err.config && !err.config._unlockRetried && hasUnlockListener()) {
      const mode = errorCodeOf(err) === "GWB001" ? "reauth" : isSessionExpired(err) ? "unlock" : null;
      if (mode) {
        try {
          await requestUnlock(mode, mode === "unlock" ? initSession : undefined);
        } catch {
          return Promise.reject(err);               // the user gave up: the caller sees the original error
        }
        const retry = { ...err.config, _unlockRetried: true };
        if (retry._originalData !== undefined) {
          retry.data = retry._originalData;         // the request interceptor encrypts it again under the new key
        }
        return gatewayApi.request(retry);
      }
    }

    // GWB004 — the login sat idle, lost its slot and the user's other logins have taken them all. Log in again.
    if (status === 403 && errorCodeOf(err) === "GWB004" && !handlesItsOwnErrors) {
      if (loggedIn) forceLogout();
      return Promise.reject(err);
    }

    if (status === 401 && !handlesItsOwnErrors) {
      const code = errorCodeOf(err);

      // GWB001 — the token is bound to another IP. Same login, prove the MPIN to re-bind (/auth/reauth).
      if (code === "GWB001") {
        if (loggedIn) goToMpinScreen("reauth");
        return Promise.reject(err);
      }

      // GWA003 / GWA004 — no token, or an expired / invalid one: only a fresh login helps.
      if (code === "GWA003" || code === "GWA004") {
        if (loggedIn) forceLogout();
        return Promise.reject(err);
      }

      // GWB002 — the gateway revoked this token (another browser used it, or too many IP changes).
      if (code === "GWB002") {
        if (loggedIn) forceLogout();
        return Promise.reject(err);
      }

      // Crypto session expired — PayloadCryptoWebFilter returns plain 401 JSON (GWC002; the message text
      // is kept for older gateways). Deliberate idle-lock: the AES session TTL (15 min) is shorter than
      // the login token's lifetime (10h), so the app re-prompts MPIN on idle without a full re-login.
      // Don't silently re-init + retry here — send the user to /mpin-verify.
      if (isSessionExpired(err)) {
        if (loggedIn) {
          try { await initSession(); } catch { /* /mpin-verify will retry the handshake */ }
          goToMpinScreen("unlock");
        }
        return Promise.reject(err);
      }
    }

    return Promise.reject(err);
  }
);

export default gatewayApi;
